/**
 * "If this broke, who would hear?"
 *
 * The report is phrased entirely in terms of CONSEQUENCE rather than
 * configuration, because the configuration is not the interesting part. "No
 * default company" means nothing to somebody skimming; "their tickets have
 * nowhere to be filed, so none is created" is a sentence that gets acted on.
 *
 * NOT LOADED AUTOMATICALLY. It walks every machine and every client, and the
 * answer changes about as often as somebody changes a setting - so it runs when
 * asked. A panel that costs a full scan on every page open is a panel that gets
 * removed later for being slow.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

type Severity = 'silent' | 'partial' | 'note';

interface Finding {
  severity: Severity;
  kind: string;
  title: string;
  consequence: string;
  fix: string;
  machines: string[];
  count: number;
}

interface Result {
  findings: Finding[];
  checked: number;
  clean: boolean;
}

const TAG: Record<Severity, { label: string; cls: string }> = {
  silent: { label: 'nobody is told', cls: 'tag-bad' },
  partial: { label: 'partly covered', cls: 'tag-warn' },
  note: { label: 'worth knowing', cls: '' },
};

export function TicketingExceptions({ orgId }: { orgId: string }) {
  const [r, setR] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  async function run() {
    setBusy(true); setErr(null);
    try {
      const res = await httpsCallable(functions, 'ticketingExceptions')({ orgId });
      setR(res.data as Result);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <section className="panel">
      <h2 className="panel-title">If a backup failed right now, who would hear?</h2>
      <p className="panel-note" style={{ marginTop: 0 }}>
        Every other screen here reports what <em>is</em> wrong. This one reports what would happen
        if something went wrong &mdash; the configurations in which a machine can fail completely
        and nobody is told anything at all. Those look exactly like a machine that is fine.
      </p>

      {err && <div className="banner banner-error">{err}</div>}

      <button className="btn" disabled={busy} onClick={() => void run()}>
        {busy ? 'Checking' : r ? 'Check again' : 'Check ticket routing'}
      </button>

      {r?.clean && (
        <div className="banner" style={{ marginTop: 12 }}>
          <strong>Nothing to report.</strong> All {r.checked} machines would raise an alert and a
          ticket if they failed.
        </div>
      )}

      {r && r.findings.length > 0 && (
        <>
          <p className="panel-note" style={{ marginBottom: 6, marginTop: 14 }}>
            {r.findings.length} finding{r.findings.length === 1 ? '' : 's'} across {r.checked} machines.
          </p>
          <table className="mini-table">
            <tbody>
              {r.findings.map((f) => (
                <tr key={f.kind}>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span className={'tag ' + TAG[f.severity].cls}>{TAG[f.severity].label}</span>
                      <strong>{f.title}</strong>
                      {f.count > 0 && (
                        <span className="dim" style={{ fontSize: 12 }}>
                          {f.count} machine{f.count === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>{f.consequence}</div>
                    <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>{f.fix}</div>

                    {f.machines.length > 0 && (
                      <>
                        <button
                          className="btn-link" style={{ marginTop: 4 }}
                          onClick={() => setOpen(open === f.kind ? null : f.kind)}
                        >
                          {open === f.kind ? 'hide the list' : 'which machines'}
                        </button>
                        {open === f.kind && (
                          <div className="mono" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.6 }}>
                            {f.machines.join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
